import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { toApiYmd } from '../../utils/dateUtil';
import { GetUseReasonDto } from '../../dataAccess/webApi/dto/useReasonDto'
import { facilityLotteryReservationSelectionUpdateUrl, facilityLotteryReservationCancelUrl } from '../common/constant/appUrl'
import { lotteryMethod, yesNo } from '../common/constant/classification'
import { OperationId } from '../common/constant/operationLog'
import { useErrorHandle } from '../common/error/errorHandler'
import { getFacility } from '../common/facility'
import { useOperationLog } from '../common/operationLog'
import { getLotteryReservation } from '../common/lotteryReservation'
import { getUseReason } from '../common/useReason'
import { showLoading } from '../common/store/slices/application'
import { LotteryReservationReference } from '../../views/components/common/lotteryReservationDetail/lotteryReservationDetailGItems'
import { 
  getLotteryReservationDecodeMstValue, 
  setStringifyNamesChildInfoList,
  stringifySelectBox
} from '../common/lotteryReservation'
import { getProjectMstDataToRefer } from '../common/projectMst'
import { selectSystemControl } from '../common/store/slices/systemControl';
import { MEDICAL_DOC_USAGE } from '../common/constant/projectMst';
import { isUseReasonCategoryHidden } from '../common/reservation';

interface UrlParams {
  lotteryReservationNo: string
}

type LotteryReservationDetail = LotteryReservationReference & {
  facilityId: string,
  canUpdate: boolean,
  canCancel: boolean,
  useReason: GetUseReasonDto,
  lotteryReservationNo: string
}

export const useAction = () => {
  const errorHandle = useErrorHandle();
  const dispatch = useDispatch();
  const history = useHistory();
  const { lotteryReservationNo } = useParams<UrlParams>();
  const { addOperationLog, attachAccessData } = useOperationLog();
  const [lotteryReservationDetail, setLotteryReservationDetail] = useState<LotteryReservationDetail>();
  const sysCtrl = useSelector(selectSystemControl)
  const showSiblingsLotteryResultReferenceFlag = sysCtrl.lotteryMethod === lotteryMethod.byParent

  useEffect(() => {
    dispatch(
      showLoading(errorHandle(async () => {
        addOperationLog({ operationId: OperationId.OP_00000170 });

        const [decodeMstValue, reservation, useReason] = await Promise.all([
          getLotteryReservationDecodeMstValue(),
          getLotteryReservation(lotteryReservationNo),
          getUseReason(),
        ]);

        const facility = await getFacility(reservation.facilityId, reservation.usageDate)
        const projectMst = await getProjectMstDataToRefer(reservation.facilityId, [reservation.usageDate], facility.projectId)
        projectMst.medicalDocUsage = projectMst.medicalDocUsage === MEDICAL_DOC_USAGE.BY_FACILITY ? facility.medicalDoc : projectMst.medicalDocUsage

        const currAnswers = {...reservation.childInfoList[0].facilityInputFields};
        reservation.childInfoList[0].facilityInputFields = {};
        facility.facilityInputFields?.forEach((field) => {
          const answer = currAnswers[field.id];
          reservation.childInfoList[0].facilityInputFields[field.id] = {
            answer: answer?.answer ?? "", 
            id: answer?.id ?? null, 
            updateDatetime: answer?.updateDatetime ?? null, 
            order: field.order,
          }
        })

        attachAccessData({
          accessData: [
            ... reservation.childInfoList.map((child) => ({
              userIdRegFlag: yesNo.yes, 
              childId: child.childId,
              usageDate: toApiYmd(reservation.usageDate), 
              lotteryReservationNo
            }))
          ],
        });
        
        setLotteryReservationDetail({
          ...reservation,
          ...facility,
          lotteryReservationNo,
          projectMst,
          useReason,
          childInfoList: setStringifyNamesChildInfoList(reservation.childInfoList, decodeMstValue),
          formUseReason: {
            ...reservation.formUseReason,
            stringify: (decodeMstValue) 
                ? stringifySelectBox(
                  decodeMstValue.formUseReason, 
                  reservation.formUseReason?.select, 
                  reservation.formUseReason?.other,
                  ["その他"]
                ): undefined
          },
          isUseReasonCategoryHidden: isUseReasonCategoryHidden(reservation.useReasonCategory, facility)
        });
      }))
    )
  }, []);

  const goCancel = useCallback(() => {
    addOperationLog({ operationId: OperationId.OP_00000171 })
    history.push(facilityLotteryReservationCancelUrl.url(lotteryReservationNo))
  },[history, lotteryReservationNo, addOperationLog]);

  const goChange = useCallback(() => {
    addOperationLog({ operationId: OperationId.OP_00000172 })
    history.push(facilityLotteryReservationSelectionUpdateUrl.url(lotteryReservationNo))
  }, [history, lotteryReservationNo, addOperationLog]);

  return {
    lotteryReservationDetail,
    showSiblingsLotteryResultReferenceFlag,
    goCancel,
    goChange
  }
}
